import type { VWOLogger } from './types';

const OVERRIDE_PARAM = 'vwo_overrides';

/**
 * Parses VWO feature flag overrides from a base64-encoded query param.
 *
 * Query param format: `?vwo_overrides=<base64-encoded JSON>`
 *
 * The JSON should be an object mapping flag keys to their variable objects:
 * `{ "showScheduler": { "showScheduler": true }, "newCheckout": { "variant": "b" } }`
 *
 * @example
 * // JSON: {"showScheduler":{"showScheduler":true}}
 * // Base64: eyJzaG93U2NoZWR1bGVyIjp7InNob3dTY2hlZHVsZXIiOnRydWV9fQ==
 * // URL: ?vwo_overrides=eyJzaG93U2NoZWR1bGVyIjp7InNob3dTY2hlZHVsZXIiOnRydWV9fQ==
 * const overrides = parseFlagOverrides(searchParams);
 * // { showScheduler: { showScheduler: true } }
 *
 * // Pass to getFeatureFlags:
 * const flags = await getFeatureFlags(keys, { ...context, flagOverrides: overrides });
 */
export function parseFlagOverrides(
	searchParams: Record<string, string | string[] | undefined>,
	logger?: VWOLogger
): Record<string, Record<string, unknown>> | undefined {
	const rawValue = getParamValue(searchParams[OVERRIDE_PARAM]);
	if (!rawValue) return undefined;

	try {
		let json: string;
		if (typeof atob === 'function') {
			json = atob(rawValue);
		} else {
			json = Buffer.from(rawValue, 'base64').toString('utf-8');
		}

		const parsed: unknown = JSON.parse(json);
		if (!isPlainObject(parsed)) {
			logger?.warn?.('Invalid vwo_overrides value — expected base64-encoded JSON object', { value: rawValue });
			return undefined;
		}

		const result: Record<string, Record<string, unknown>> = {};
		for (const [key, val] of Object.entries(parsed)) {
			if (isPlainObject(val)) {
				result[key] = val;
			} else {
				logger?.warn?.(`Invalid override for flag "${key}" in vwo_overrides — expected object`, {
					key,
					value: val,
				});
			}
		}
		return Object.keys(result).length > 0 ? result : undefined;
	} catch {
		logger?.warn?.('Failed to decode vwo_overrides — expected base64-encoded JSON', { value: rawValue });
		return undefined;
	}
}

/**
 * Creates a base64-encoded override string for use in preview links.
 *
 * @example
 * const encoded = encodeFlagOverrides({
 *   showScheduler: { showScheduler: true },
 *   newCheckout: { variant: 'b' },
 * });
 * const previewUrl = `https://example.com?vwo_overrides=${encoded}`;
 */
export function encodeFlagOverrides(overrides: Record<string, Record<string, unknown>>): string {
	const json = JSON.stringify(overrides);
	if (typeof btoa === 'function') {
		return btoa(json);
	}
	return Buffer.from(json).toString('base64');
}

/**
 * Builds the full query param string for a VWO feature flag preview link.
 *
 * @param flagKey - The feature flag key
 * @param variableKey - The variable key within the flag
 * @param value - The value to force for the variable
 * @returns The full query string (e.g. `vwo_overrides=eyJ...`) ready to append to a URL
 *
 * @example
 * const param = buildFlagOverrideParam('showScheduler', 'showScheduler', true);
 * const previewUrl = `https://example.com?${param}`;
 */
export function buildFlagOverrideParam(flagKey: string, variableKey: string, value: unknown): string {
	const encoded = encodeFlagOverrides({
		[flagKey]: { [variableKey]: value },
	});
	return `${OVERRIDE_PARAM}=${encoded}`;
}

function getParamValue(value: string | string[] | undefined): string | undefined {
	return Array.isArray(value) ? value[0] : value;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null && !Array.isArray(value);
}
